if (!customElements.get('popup-element')) {
  customElements.define('popup-element', class PopupElement extends HTMLElement {
    constructor() {
      super();
    }

    async connectedCallback() {
      
      this.popups = this.parseJson(this.dataset.popups, []);
      this.isGatedArchiveSale = this.dataset.type === 'gated-archive-sale';
      this.countryCode = await window.getCountryCode();
      this.encryptedWildcard = await this.encrypt('*')

      const visiblePopups = await Promise.all(
        this.popups.map(async (popup) => {
          const shouldShow = await this.shouldShowPopup(popup);
          return shouldShow ? popup : null;
        })
      );

      const filteredPopups = visiblePopups.filter(popup => popup !== null);

      for (const popup of filteredPopups) {
        await this.buildPopup(popup);
      }
    }

    parseJson(str, defaultValue = null) {
      try {
        return JSON.parse(str);
      } catch (_e) {
        return defaultValue;
      }
    }

    async updateCartAttributes(attributes) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json'
          },
          body: JSON.stringify({ attributes: attributes })
        });

        if (response.ok) {
          console.log('Attributes updated successfully');
          // Optionally, reload the page to apply changes
          location.reload();
        } else {
          console.error('Failed to update attributes:', response.statusText);
        }
      } catch (error) {
          console.error('Error updating attributes:', error);
      }
    }

    async shouldShowPopup(popup) {
      // Fetch the cart data to check if passwordEntered is true
      const cartData = await fetch('/cart.js').then(response => response.json());
    
      // Check if the passwordEntered attribute is true and if the popup type is password
      if (cartData?.attributes?.passwordEntered === true && popup.type === 'password') {
        console.log('Password already entered, skipping password popup');
        return false; // Skip the password popup
      }
    
      // Otherwise, continue with the existing conditions for showing the popup
      return !( !popup || this.hasCookie(popup) ) && (
        await this.isUrlMatched(popup.displayCondition.pageUrls)
        && await this.isParametersMatched(popup.displayCondition.queryParameters)
        && await this.isCountryCodeMatched(popup.displayCondition.countryCodes)
      );
    }
    

    async isUrlMatched(conditionUrls, isEncrypted = false) {
      if (!conditionUrls || conditionUrls.length === 0) {
        return false;
      }

      const pathName = window.location.pathname;

      const result = await Promise.all(conditionUrls.map(async (url) => {
        if (isEncrypted) {
          const encryptedPathName = await this.encrypt(pathName);

          return url === this.encryptedWildcard || url === encryptedPathName;
        } else {
          return url === '*' || url === pathName;
        }
      }));

      return result.some(Boolean);
    }

    async isParametersMatched(conditionParameters, isEncrypted = false) {
      if (!conditionParameters || conditionParameters.length === 0) {
        return false;
      }

      const { searchParams } = new URL(window.location.href);

      const result = await Promise.all(conditionParameters.map(async (parameter) => {
        const nameValuePairs = parameter.split('&');

        // Use Promise.all to handle async logic inside .some
        const pairResults = await Promise.all(nameValuePairs.map(async (pair) => {
          const [name, value = null] = pair.split('=');

          if (!value) {
            return name === '*' || searchParams.has(name);
          } else {
            const paramValue = searchParams.get(name);

            if (isEncrypted) {
              const encryptedValue = await this.encrypt(paramValue);
              return value === this.encryptedWildcard || value === encryptedValue;
            }

            return value === '*' || value === paramValue;
          }
        }));

        // Check if any pair returns true
        return pairResults.some(Boolean);
      }));

      return result.some(Boolean);
    }

    async isCountryCodeMatched(conditionCountryCodes, isEncrypted = false) {
      if (!conditionCountryCodes || conditionCountryCodes.length === 0) {
        return false;
      }

      const result = await Promise.all(conditionCountryCodes.map(async (countryCode) => {
        if (isEncrypted) {
          const encryptedCountryCode = await this.encrypt(this.countryCode);

          return countryCode === this.encryptedWildcard || countryCode === encryptedCountryCode;
        }

        return countryCode === '*' || countryCode === this.countryCode;
      }));

      return result.some(Boolean);
    }

    hasCookie(popup) {
      if (popup.cookieExpiration === 'always') {
        return false;
      }

      return document.cookie.match(new RegExp(`(^| )popup-${popup.id}=([^;]+)`));
    }

    async buildPopup(popup) {
      return new Promise(async (resolve) => {
        if (popup.type === 'password' && await this.shouldBypassPopup(popup)) {
          if (this.isGatedArchiveSale) {
       
            // Attributes to update when password is correct
            const attributesToUpdate = {
                'gatedArchiveSale': true,
                'passwordEntered': true,
            };
            // Send the updated attributes to the server
            this.updateCartAttributes(attributesToUpdate);
            location.reload();
          }
          resolve();
          return;
        }

        const wrapper = this.createElement('div', `popup__overlay popup--${popup.type}`, document.body);
        const modal = this.createElement('div', 'popup__modal', wrapper, {
          role: 'dialog',
          'aria-modal': true,
          'aria-label': this.stripHtml(popup.title)
        });
        const modalInner = this.createElement('div', 'popup__modal-inner', modal);

        if (popup.type !== 'password') {
          const closeButton = this.createElement('button', 'popup__modal-close', modalInner);
          closeButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" role="img"> <title>Close</title> <path fill="#121211" fill-rule="nonzero" d="m17.206 14.977-6.005-6.005 6.005-6.005c.274-.273.495-1.238-.248-1.98-.742-.744-1.707-.522-1.98-.249L8.971 6.744 2.967.739C2.694.465 1.73.243.987.986.242 1.73.464 2.694.738 2.967l6.005 6.005-6.005 6.005c-.274.274-.496 1.238.247 1.981.743.743 1.708.522 1.981.248l6.005-6.005 6.005 6.005c.274.274 1.239.495 1.982-.248.742-.743.52-1.707.247-1.98Z"/> </svg>';

          closeButton.addEventListener('click', () => {
            this.closeModal(wrapper, popup);
            resolve();
          });

          wrapper.classList.add('is-clickable');
          wrapper.addEventListener('click', (event) => {
            if (event.target == wrapper) {
              this.closeModal(wrapper, popup);
              resolve();
            }
          });
        }

        if (popup.image && popup.image.url) {
          const modalImage = this.createElement('img', 'popup__modal-image', modalInner, {
            src: popup.image.url,
            alt: popup.image.alt || popup.title
          });
        }

        if (popup.image.maxWidthMobile > 0) {
          wrapper.style.setProperty('--popup-image-max-width-mobile', popup.image.maxWidthMobile + 'px');
        }

        if (popup.image.maxWidthDesktop > 0) {
          wrapper.style.setProperty('--popup-image-max-width-desktop', popup.image.maxWidthDesktop + 'px');
        }

        if (popup.title) {
          const modalTitle = this.createElement('div', 'popup__modal-title', modalInner);
          modalTitle.innerHTML = popup.title;
        }

        if (popup.description) {
          const modalDescription = this.createElement('div', 'popup__modal-description', modalInner);
          modalDescription.innerHTML = popup.description;
        }

        if (popup.type === 'password') {
          const passwordForm = this.createElement('form', 'popup__password-form', modalInner);

          if (popup.password.inputLabel) {
            const passwordInputLabel = this.createElement('label', 'popup__password-input-label', passwordForm, {
              for: 'popup-password-input'
            });
            passwordInputLabel.innerHTML = popup.password.inputLabel;
          }

          const passwordInput = this.createElement('input', 'popup__password-input', passwordForm, {
            type: 'password',
            id: 'popup-password-input',
            name: 'password',
            placeholder: popup.password.inputPlaceholder
          });

          if (popup.password.errorMessage) {
            const passwordErrorMessage = this.createElement('div', 'popup__password-error-message', passwordForm);
            passwordErrorMessage.innerHTML = popup.password.errorMessage;
          }

          const passwordSubmitButton = this.createElement('button', 'popup__modal-cta-button', passwordForm, {
            type: 'submit'
          });
          passwordSubmitButton.textContent = popup.password.buttonLabel || 'Access Now';

          passwordForm.addEventListener('submit', async (event) => {
            event.preventDefault();
        
            const formData = new FormData(passwordForm);
            const password = formData.get('password');
        
            if (popup.password.password === await this.encrypt(password.toLowerCase())) {
                passwordForm.classList.remove('is-error');
                this.closeModal(wrapper, popup);
        
                if (this.isGatedArchiveSale) {
       
                  // Attributes to update when password is correct
                  const attributesToUpdate = {
                      'gatedArchiveSale': true,
                      'passwordEntered': true,
                  };
                  // Send the updated attributes to the server
                  this.updateCartAttributes(attributesToUpdate);
                }
                
                resolve();
            } else {
                passwordForm.classList.add('is-error');
            }
          });
        
        } else {
          if (popup.ctaLabel) {
            if (popup.ctaUrl && popup.ctaUrl != document.location.pathname) {
              const modalCtaLink = this.createElement('a', 'button popup__modal-cta-button', modalInner, {
                href: popup.ctaUrl
              });

              modalCtaLink.textContent = popup.ctaLabel;

              // <a> tag from createElement lacks linking functionality
              modalCtaLink.addEventListener('click', () => {
                document.location.href = popup.ctaUrl;
              });
            } else {
              const modalCtaButton = this.createElement('button', 'popup__modal-cta-button', modalInner, {
                type: 'button'
              });
              modalCtaButton.textContent = popup.ctaLabel || 'Close';

              modalCtaButton.addEventListener('click', () => {
                this.closeModal(wrapper, popup);
                resolve();
              });
            }
          }
        }

        if (popup.footerNote) {
          const modalNote = this.createElement('div', 'popup__modal-footer-note', modalInner);
          modalNote.innerHTML = popup.footerNote;
        }

        if (popup.footerTextLinkCopy) {
          if (popup.footerTextLinkUrl && popup.footerTextLinkUrl != '/') {
            const modalFooterLink = this.createElement('a', 'popup__modal-footer-link', modalInner, {
              href: popup.footerTextLinkUrl
            });

            modalFooterLink.textContent = popup.footerTextLinkCopy;

            // <a> tag from createElement lacks linking functionality
            modalFooterLink.addEventListener('click', () => {
              document.location.href = popup.footerTextLinkUrl;
            });
          } else {
            const modalFooterLinkClose = this.createElement('button', 'popup__modal-footer-link', modalInner, {
              type: 'button'
            });
            modalFooterLinkClose.textContent = popup.footerTextLinkCopy;

            modalFooterLinkClose.addEventListener('click', () => {
              this.closeModal(wrapper, popup);
              resolve();
            });
          }
        }

        if (!this.openModal(wrapper, popup)) {
          resolve();
        }
      });
    }

    async shouldBypassPopup(popup) {
      return await this.isUrlMatched(popup.bypassCondition.pageUrls, true)
        && await this.isParametersMatched(popup.bypassCondition.queryParameters, true)
        && await this.isCountryCodeMatched(popup.bypassCondition.countryCodes, true)
    }

    createElement(tagName, className, parent = null, attributes = {}) {
      const el = document.createElement(tagName);
      el.className = className;

      for (const property in attributes) {
        el.setAttribute(property, attributes[property]);
      }

      if (parent) {
        parent.appendChild(el);
      }

      return el;
    }

    stripHtml(html) {
      const el = document.createElement('div');
      el.innerHTML = html;

      return el.textContent;
    }

    openModal(wrapper, popup) {
      if (popup.reopenPopup === 'always' || !this.hasCookie(popup)) {
        if (popup.type === 'password') {
          document.body.classList.add('has-popup-active');
        }

        wrapper.classList.add('is-open');
        const focusableEls = getFocusableElements(wrapper);

        if (focusableEls.length > 0) {
          trapFocus(wrapper, focusableEls[0]);
        }

        return true;
      }

      return false;
    }

    closeModal(wrapper, popup) {
      document.body.classList.remove('has-popup-active');
      wrapper.classList.remove('is-open');

      const focusableEls = getFocusableElements(document);
      if (focusableEls.length > 0) {
        removeTrapFocus(focusableEls[0]);
      }

      this.saveCookie(popup);
    }

    saveCookie(popup, cookieContent = null) {
      if (popup.cookieExpiration !== 'always') {
        const numberOfDays = popup.cookieExpiration === 'never' ? 400 : popup.cookieExpirationNumberOfDays;
        const date = new Date();
        date.setTime(date.getTime() + numberOfDays * 24 * 60 * 60 * 1000);

        if (!cookieContent) {
          cookieContent = `popup-${popup.id}=1`;
        }

        document.cookie = `${cookieContent}; expires=${date.toUTCString()}; path=/`;
      }
    }

    saveCookieGatedArchiveSale(popup) {
      this.saveCookie(popup, 'is_invite_user=true');
    }

    async encrypt(text) {
      const encoder = new TextEncoder();
      const data = encoder.encode(text);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

      return hashHex;
    }
  });
}
